import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { STATS } from "@/data";
import { useLang } from "@/i18n";

function Counter({ value, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(decimals ? "0.0" : "0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString("en-US")),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);

  return (
    <span ref={ref} dir="ltr" className="tabular-nums">
      {display}
      {suffix}
    </span>
  );
}

export function StatsBar() {
  const { lang } = useLang();
  return (
    <section data-testid="stats-banner" data-always-dark className="relative border-y border-blue-500/15 bg-[#0A1222]/70">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`stat-${s.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative px-5 py-10 lg:px-8 border-blue-500/10 [&:not(:first-child)]:border-s lg:border-s-0 lg:[&:not(:first-child)]:border-s"
            >
              <div className="text-3xl lg:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-b from-blue-300 to-blue-600">
                {s.static ? s.static : <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />}
              </div>
              <div className="mt-2 text-sm font-semibold text-white">{s[`label_${lang}`]}</div>
              <div className="mt-1 text-xs text-slate-400">{s[`detail_${lang}`]}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
